import { useRef, useEffect } from "react";
import classNames from "classnames";
import Avatar from "@/components/Avatar";
import type { MentionUser } from "./types";

function StatusMessage({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return (
    <div className="py-3 px-4 text-center text-body-medium text-sys-brand-on-surface-variant" data-testid={testId}>
      {children}
    </div>
  );
}

interface AtMentionOverlayProps {
  users: MentionUser[];
  isLoading?: boolean;
  query: string;
  selectedIndex: number;
  onSelect: (user: MentionUser) => void;
  onHighlightChange: (index: number) => void;
  className?: string;
}

export default function AtMentionOverlay({
  users,
  isLoading = false,
  query,
  selectedIndex,
  onSelect,
  onHighlightChange,
  className
}: AtMentionOverlayProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  itemRefs.current = itemRefs.current.slice(0, users.length);

  useEffect(() => {
    if (selectedIndex < 0 || !listRef.current) return;
    const item = itemRefs.current[selectedIndex];
    if (!item) return;

    const parent = listRef.current;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const parentTop = parent.scrollTop;
    const parentBottom = parentTop + parent.clientHeight;

    if (itemTop < parentTop) {
      parent.scrollTo({ top: itemTop });
    } else if (itemBottom > parentBottom) {
      parent.scrollTo({ top: itemBottom - parent.clientHeight });
    }
  }, [selectedIndex]);

  const hasQuery = query.trim().length > 0;

  return (
    <div
      className={classNames(
        "bg-sys-brand-surface shadow-md rounded-md max-h-60 min-w-[420px] overflow-y-auto",
        className
      )}
      ref={listRef}
      role="listbox"
      aria-label="Mention a teammate"
      data-testid="at-mention-overlay"
    >
      {!hasQuery && !isLoading && <StatusMessage testId="at-mention-overlay-prompt">Mention a teammate</StatusMessage>}

      {isLoading && <StatusMessage testId="at-mention-overlay-loading">Searching...</StatusMessage>}

      {hasQuery && !isLoading && users.length === 0 && (
        <StatusMessage testId="at-mention-overlay-empty">No teammates found</StatusMessage>
      )}

      {!isLoading &&
        users.map((user, index) => (
          <div
            key={user.id}
            id={`at-mention-option-${user.id}`}
            role="option"
            aria-selected={index === selectedIndex}
            className={classNames(
              "flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors hover:bg-sys-brand-surface-container-high",
              index === selectedIndex && "bg-sys-brand-surface-container-high"
            )}
            onClick={() => onSelect(user)}
            onMouseEnter={() => onHighlightChange(index)}
            ref={(el) => (itemRefs.current[index] = el)}
            data-testid={`at-mention-option-${user.id}`}
          >
            <Avatar picture={user.photo} size="small" />
            <span className="text-body-medium-semibold text-sys-brand-on-surface truncate">
              {user.firstName} {user.lastName}
            </span>
            <span className="text-body-small text-sys-brand-on-surface-variant truncate ml-auto">{user.email}</span>
          </div>
        ))}
    </div>
  );
}
